import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "@/lib/api";
import { ArrowRightLeft, MapPin, FileText, History, Pencil, Trash2, Search, PackageSearch, Users, ChevronDown, ChevronRight, Lock, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { triggerBulkLabelPrint } from "@/lib/labelPrint";
import LabelFormatBadge from "@/components/label/LabelFormatBadge";

const LOCATIONS = ["Kani", "Naroda"];
const ALL = "__ALL__";

export default function ShiftStock() {
  const { user } = useAuth();
  const role = user?.role;
  // Production: ONLY move tab; Dispatch: everything EXCEPT move; Admin: everything
  const canMove = ["admin", "production"].includes(role);
  const canSeeOtherTabs = ["admin", "dispatch"].includes(role);
  const canEditShift = role === "admin";   // edit/delete shift log require admin approval for others
  const canDeleteShift = role === "admin";
  const [products, setProducts] = useState([]);
  // boxesByProduct: { [productId]: FinishedBox[] (in_stock only) }
  const [boxesByProduct, setBoxesByProduct] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(ALL);
  const [filterLoc, setFilterLoc] = useState("Kani");
  // selected stores full carton objects keyed by id so they persist across category switches
  const [selected, setSelected] = useState({});
  const [toLoc, setToLoc] = useState("Naroda");
  const [busy, setBusy] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [tab, setTab] = useState(canMove ? "move" : "log");

  // Carton Tracker state
  const [trackQuery, setTrackQuery] = useState("");
  const [trackData, setTrackData] = useState(null);
  const [trackBusy, setTrackBusy] = useState(false);

  // Customer Dispatch state
  const [dispatchGroups, setDispatchGroups] = useState([]);
  const [expandedCust, setExpandedCust] = useState({});
  const [selectedChallan, setSelectedChallan] = useState({}); // {customer_id: challan_key}

  const loadInit = async () => {
    const [p, s] = await Promise.all([api.get("/products"), api.get("/cartons/shifts")]);
    setProducts(p.data);
    setShifts(s.data);
    const map = {};
    for (const prod of p.data) {
      const r = await api.get(`/products/${prod.id}/boxes`);
      map[prod.id] = r.data.filter((b) => b.status === "in_stock");
    }
    setBoxesByProduct(map);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInit().catch(() => {}); }, []);

  const productById = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.id] = p; });
    return m;
  }, [products]);

  // All in-stock cartons across categories with sku attached
  const allCartons = useMemo(() => {
    const out = [];
    Object.entries(boxesByProduct).forEach(([pid, list]) => {
      list.forEach((b) => out.push({ ...b, sku: productById[pid]?.sku || "" }));
    });
    return out;
  }, [boxesByProduct, productById]);

  // per-category Kani/Naroda counts (for option labels)
  const stockSummary = useMemo(() => {
    const out = {};
    Object.entries(boxesByProduct).forEach(([pid, list]) => {
      out[pid] = {
        kani: list.filter((b) => (b.location || "Kani") === "Kani").length,
        naroda: list.filter((b) => b.location === "Naroda").length,
      };
    });
    const all = { kani: 0, naroda: 0 };
    Object.values(out).forEach((s) => { all.kani += s.kani; all.naroda += s.naroda; });
    return { ...out, [ALL]: all };
  }, [boxesByProduct]);

  // cartons visible in Available pane = filter by category (or all) + by from-location + exclude selected
  const available = useMemo(() => {
    let list = allCartons;
    if (selectedProduct !== ALL) list = list.filter((b) => b.product_id === selectedProduct);
    list = list.filter((b) => (b.location || "Kani") === filterLoc && !selected[b.id]);
    return list;
  }, [allCartons, selectedProduct, filterLoc, selected]);

  const chosen = useMemo(() => Object.values(selected), [selected]);

  const pick = (carton) => setSelected((s) => ({ ...s, [carton.id]: carton }));
  const drop = (id) => setSelected((s) => { const n = { ...s }; delete n[id]; return n; });
  const clearSelection = () => setSelected({});

  const totalNet = chosen.reduce((s, c) => s + c.net_weight_kg, 0);
  const totalGross = chosen.reduce((s, c) => s + c.gross_weight_kg, 0);

  // sanity: ensure all chosen are at the same source location (else block move)
  const chosenFromLocs = Array.from(new Set(chosen.map((c) => c.location || "Kani")));
  const mixedSource = chosenFromLocs.length > 1;

  const move = async () => {
    if (chosen.length === 0) return toast.error("Select cartons");
    if (mixedSource) return toast.error("Selected cartons are at different locations. Pick from one source.");
    if (toLoc === chosenFromLocs[0]) return toast.error("Source and destination are same");
    setBusy(true);
    try {
      const { data } = await api.post("/cartons/move", { carton_ids: chosen.map((c) => c.id), to_location: toLoc });
      toast.success(`Moved ${data.moved} cartons → ${data.to_location} (${data.shift_no}). Download challan from Shift Log.`);
      setSelected({});
      loadInit();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadChallan = async (shift, fmt = "pdf") => {
    const token = localStorage.getItem(TOKEN_KEY);
    const resp = await fetch(`${BACKEND_URL}/api/cartons/shifts/${shift.id}/challan.${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${shift.shift_no}.${fmt}`;
    a.click();
  };

  const deleteShift = async (shift) => {
    if (!window.confirm(`Delete shift ${shift.shift_no}? Cartons that have NOT been dispatched will be moved back to ${shift.from_location}.`)) return;
    try {
      const { data } = await api.delete(`/cartons/shifts/${shift.id}`);
      toast.success(`Deleted ${shift.shift_no} — ${data.reverted} cartons reverted to ${shift.from_location}${data.skipped_dispatched ? ` (${data.skipped_dispatched} already dispatched, unchanged)` : ""}`);
      loadInit();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const editShift = async (shift) => {
    if (!window.confirm(`Edit shift ${shift.shift_no}? Cartons will first be reverted to ${shift.from_location}, then loaded into the Move tab so you can re-pick.`)) return;
    try {
      const { data } = await api.put(`/cartons/shifts/${shift.id}`);
      // Reload then prefill selection
      const [p, s] = await Promise.all([api.get("/products"), api.get("/cartons/shifts")]);
      setProducts(p.data);
      setShifts(s.data);
      const map = {};
      const selMap = {};
      for (const prod of p.data) {
        const r = await api.get(`/products/${prod.id}/boxes`);
        const inStock = r.data.filter((b) => b.status === "in_stock");
        map[prod.id] = inStock;
        for (const b of inStock) {
          if (data.carton_ids.includes(b.id)) selMap[b.id] = b;
        }
      }
      setBoxesByProduct(map);
      setSelected(selMap);
      setFilterLoc(data.from_location);
      setToLoc(data.to_location);
      setTab("move");
      toast.success(`${shift.shift_no} reverted · ${Object.keys(selMap).length} cartons pre-selected — adjust & confirm to re-shift`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const lookupCarton = async (q) => {
    const box = (q || "").trim().toUpperCase();
    if (!box) return;
    setTrackBusy(true);
    setTrackData(null);
    try {
      const { data } = await api.get(`/cartons/${encodeURIComponent(box)}/activity`);
      setTrackData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Carton not found");
    } finally { setTrackBusy(false); }
  };

  const loadDispatches = async () => {
    try {
      const { data } = await api.get("/dispatches/by-customer");
      setDispatchGroups(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  useEffect(() => { if (tab === "customers") loadDispatches(); }, [tab]);

  const tCls = (k) => `px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 -mb-px ${tab === k ? "border-orange-600 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"}`;
  const allSummary = stockSummary[ALL] || { kani: 0, naroda: 0 };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Inventory Movement</div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Shift Stock Location</h1>
      </div>

      <div className="border-b border-zinc-300 flex gap-1 flex-wrap">
        {canMove && (
          <button onClick={() => setTab("move")} className={tCls("move")} data-testid="tab-move"><ArrowRightLeft size={12} className="inline mr-1" /> Move</button>
        )}
        {canSeeOtherTabs && (
          <>
            <button onClick={() => setTab("log")} className={tCls("log")} data-testid="tab-log"><History size={12} className="inline mr-1" /> Shift Log ({shifts.length})</button>
            <button onClick={() => setTab("track")} className={tCls("track")} data-testid="tab-track"><PackageSearch size={12} className="inline mr-1" /> Carton Tracker</button>
            <button onClick={() => setTab("customers")} className={tCls("customers")} data-testid="tab-customers"><Users size={12} className="inline mr-1" /> Customer Dispatches</button>
          </>
        )}
      </div>

      {tab === "move" && (
        <>
          <div className="bg-white border border-zinc-300 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-[10px] font-semibold uppercase mb-1 block">Category (Kani · Naroda)</label>
              <select data-testid="shift-product" value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)} className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono">
                <option value={ALL}>★ All Stock (Kani: {allSummary.kani} · Naroda: {allSummary.naroda})</option>
                {products.map((p) => {
                  const s = stockSummary[p.id] || { kani: 0, naroda: 0 };
                  return <option key={p.id} value={p.id}>{p.sku} (Kani: {s.kani} · Naroda: {s.naroda})</option>;
                })}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">From</label>
              <select data-testid="shift-from" value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono">
                {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase mb-1 block">To</label>
              <select data-testid="shift-to" value={toLoc} onChange={(e) => setToLoc(e.target.value)} className="w-full h-9 px-2 text-sm border border-zinc-300 bg-white font-mono">
                {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>

          {chosen.length > 0 && (
            <div className="text-[11px] font-mono text-zinc-600 -mt-3">
              Tip: switch the category above to add more cartons from a different SKU — your selection is preserved.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Available */}
            <div className="bg-white border border-zinc-300" data-testid="available-table">
              <div className="px-4 py-2 border-b border-zinc-300 flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  Available at {filterLoc} {selectedProduct === ALL ? "· All Categories" : `· ${productById[selectedProduct]?.sku || ""}`}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid="shift-select-all"
                    onClick={() => {
                      const allPicked = available.length > 0 && available.every((b) => selected[b.id]);
                      if (allPicked) {
                        setSelected((s) => { const n = { ...s }; available.forEach((b) => { delete n[b.id]; }); return n; });
                      } else {
                        setSelected((s) => { const n = { ...s }; available.forEach((b) => { n[b.id] = b; }); return n; });
                      }
                    }}
                    className="text-[10px] uppercase font-mono px-2 py-0.5 border border-zinc-300 hover:bg-zinc-100"
                    title="Pick / un-pick all visible cartons"
                  >Select All</button>
                  <div className="text-xs font-mono">{available.length} cartons</div>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-zinc-50 text-zinc-500 uppercase text-[9px] font-mono tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5 border-b border-zinc-200">Box No</th>
                      <th className="text-left px-3 py-1.5 border-b border-zinc-200">Category</th>
                      <th className="text-left px-3 py-1.5 border-b border-zinc-200">Date</th>
                      <th className="text-right px-3 py-1.5 border-b border-zinc-200">Net</th>
                      <th className="text-right px-3 py-1.5 border-b border-zinc-200">Gross</th>
                      <th className="px-3 py-1.5 border-b border-zinc-200"></th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {available.length === 0 ? (
                      <tr><td colSpan={6} className="text-center text-zinc-500 py-8 text-sm font-sans">No cartons at {filterLoc}</td></tr>
                    ) : available.map((b) => (
                      <tr key={b.id} className="border-b border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => pick(b)}>
                        <td className="px-3 py-2 font-semibold">{b.box_no}</td>
                        <td className="px-3 py-2 text-xs text-zinc-700">{b.sku}</td>
                        <td className="px-3 py-2 text-xs">{b.production_date}</td>
                        <td className="px-3 py-2 text-right">N {b.net_weight_kg.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-orange-700">G {b.gross_weight_kg.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right"><button className="text-[10px] uppercase font-mono px-2 py-0.5 bg-orange-600 text-white">Pick →</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Selected */}
            <div className="bg-white border border-orange-300" data-testid="selected-table">
              <div className="px-4 py-2 border-b border-orange-300 bg-orange-50 flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Selected for shift → {toLoc}</div>
                <div className="text-xs font-mono">{chosen.length} cartons · Net {totalNet.toFixed(2)} · Gross {totalGross.toFixed(2)}</div>
              </div>
              {mixedSource && (
                <div className="px-4 py-2 bg-orange-100 border-b border-orange-300 text-[11px] font-mono text-orange-800">
                  ⚠ Selection mixes cartons from {chosenFromLocs.join(" & ")}. Remove one source to shift.
                </div>
              )}
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-orange-50 text-orange-700 uppercase text-[9px] font-mono tracking-wider sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5 border-b border-orange-200">Box No</th>
                      <th className="text-left px-3 py-1.5 border-b border-orange-200">Category</th>
                      <th className="text-left px-3 py-1.5 border-b border-orange-200">From</th>
                      <th className="text-right px-3 py-1.5 border-b border-orange-200">Net</th>
                      <th className="text-right px-3 py-1.5 border-b border-orange-200">Gross</th>
                      <th className="px-3 py-1.5 border-b border-orange-200"></th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {chosen.length === 0 ? (
                      <tr><td colSpan={6} className="text-center text-zinc-500 py-8 text-sm font-sans">Click &quot;Pick&quot; to add cartons</td></tr>
                    ) : chosen.map((b) => (
                      <tr key={b.id} className="border-b border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => drop(b.id)}>
                        <td className="px-3 py-2 font-semibold">{b.box_no}</td>
                        <td className="px-3 py-2 text-xs text-zinc-700">{b.sku}</td>
                        <td className="px-3 py-2 text-xs"><MapPin size={9} className="inline" /> {b.location || "Kani"}</td>
                        <td className="px-3 py-2 text-right">N {b.net_weight_kg.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-orange-700">G {b.gross_weight_kg.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right"><button className="text-[10px] uppercase font-mono px-2 py-0.5 border border-zinc-400 text-zinc-700">✕ Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {chosen.length > 0 && (
                <div className="border-t border-orange-300 p-3 bg-orange-50 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button data-testid="shift-clear" onClick={clearSelection} className="text-[10px] uppercase tracking-wider font-mono px-3 py-2 border border-zinc-400 text-zinc-700 hover:bg-zinc-100">Clear All</button>
                    <LabelFormatBadge />
                    <button
                      data-testid="shift-print-labels"
                      onClick={() => {
                        const boxNos = chosen.map((c) => c.box_no).filter(Boolean);
                        if (boxNos.length === 0) return;
                        triggerBulkLabelPrint(boxNos);
                        toast.success(`Sent ${boxNos.length} label${boxNos.length === 1 ? "" : "s"} to printer`);
                      }}
                      className="text-[10px] uppercase tracking-wider font-mono px-3 py-2 border border-orange-500 text-orange-700 bg-white hover:bg-orange-50 inline-flex items-center gap-1"
                      title="Re-print labels for the selected cartons before loading"
                    ><Printer size={11} /> Print labels</button>
                  </div>
                  <button data-testid="shift-confirm" onClick={move} disabled={busy || mixedSource} className="bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono px-6 py-2 flex items-center gap-2 disabled:opacity-50">
                    <ArrowRightLeft size={14} /> Shift {chosen.length} cartons → {toLoc}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {tab === "log" && (
        <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="shift-log">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Shift No</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Date</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">From</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">To</th>
                <th className="text-right px-3 py-2 border-b border-zinc-300">Cartons</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">By</th>
                <th className="text-left px-3 py-2 border-b border-zinc-300">Actions</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {shifts.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-12 text-sm font-sans">No shifts recorded yet.</td></tr>
              ) : shifts.map((s) => (
                <tr key={s.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                  <td className="px-3 py-2 font-semibold text-zinc-900">{s.shift_no}</td>
                  <td className="px-3 py-2">{s.date}</td>
                  <td className="px-3 py-2"><MapPin size={10} className="inline" /> {s.from_location}</td>
                  <td className="px-3 py-2 text-orange-700 font-semibold"><MapPin size={10} className="inline" /> {s.to_location}</td>
                  <td className="px-3 py-2 text-right">{s.count}</td>
                  <td className="px-3 py-2 text-[10px]">{s.by}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      data-testid={`shift-pdf-${s.id}`}
                      onClick={() => downloadChallan(s, "pdf")}
                      className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                      title="Download Internal Delivery Challan as PDF"
                    ><FileText size={10} /> PDF</button>
                    <button
                      data-testid={`shift-xlsx-${s.id}`}
                      onClick={() => downloadChallan(s, "xlsx")}
                      className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                      title="Download Internal Delivery Challan as XLS"
                    ><FileText size={10} /> XLS</button>
                    {canEditShift ? (
                      <button
                        data-testid={`shift-edit-${s.id}`}
                        onClick={() => editShift(s)}
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 mr-1"
                        title="Revert this shift & re-pick cartons"
                      ><Pencil size={10} /> Edit</button>
                    ) : (
                      <span
                        data-testid={`shift-edit-${s.id}-admin-only`}
                        title="Editing a shift log requires admin approval"
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-dashed border-zinc-300 text-zinc-400 px-2 py-1 mr-1 cursor-not-allowed select-none"
                      ><Lock size={10} /> Edit</span>
                    )}
                    {canDeleteShift ? (
                      <button
                        data-testid={`shift-delete-${s.id}`}
                        onClick={() => deleteShift(s)}
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-300 px-2 py-1 hover:bg-zinc-100 text-zinc-700"
                        title="Delete shift & revert non-dispatched cartons"
                      ><Trash2 size={10} /> Delete</button>
                    ) : (
                      <span
                        data-testid={`shift-delete-${s.id}-admin-only`}
                        title="Deleting a shift log requires admin approval"
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-dashed border-zinc-300 text-zinc-400 px-2 py-1 cursor-not-allowed select-none"
                      ><Lock size={10} /> Delete</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-200 text-[10px] font-mono text-zinc-600">
            Edit / Delete will revert non-dispatched cartons to their original location. Already-dispatched cartons are not touched.
          </div>
        </div>
      )}

      {tab === "track" && (
        <div className="space-y-4" data-testid="carton-tracker">
          <div className="bg-white border border-zinc-300 p-4">
            <label className="text-[10px] font-semibold uppercase mb-1 block">Search Carton (Box No.)</label>
            <div className="flex gap-2">
              <input
                data-testid="track-input"
                value={trackQuery}
                onChange={(e) => setTrackQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && lookupCarton(trackQuery)}
                placeholder="e.g. WPL262600001"
                className="flex-1 h-10 px-3 text-sm border border-zinc-300 bg-white font-mono uppercase"
              />
              <button
                data-testid="track-submit"
                onClick={() => lookupCarton(trackQuery)}
                disabled={trackBusy}
                className="bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono px-5 flex items-center gap-2 disabled:opacity-50"
              ><Search size={12} /> Track</button>
            </div>
            <div className="text-[10px] font-mono text-zinc-500 mt-2">
              Enter the system-generated carton number (format <span className="text-orange-700">WPL2626XXXXX</span>) to see its full lifecycle.
            </div>
          </div>

          {trackData && (
            <div className="bg-white border border-zinc-300" data-testid="track-result">
              <div className="px-4 py-3 border-b border-zinc-300 bg-zinc-50 grid grid-cols-1 md:grid-cols-5 gap-3 text-xs font-mono">
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Box No</div><div className="font-semibold text-zinc-950 text-base">{trackData.box_no}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Category</div><div className="font-semibold">{trackData.sku || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Net / Gross</div><div className="font-semibold">{trackData.net_weight_kg.toFixed(2)} / <span className="text-orange-700">{trackData.gross_weight_kg.toFixed(2)}</span> kg</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Current Location</div><div className="font-semibold"><MapPin size={10} className="inline" /> {trackData.current_location}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">Status</div><div className={`font-semibold uppercase ${trackData.status === "dispatched" ? "text-orange-700" : ""}`}>{trackData.status}</div></div>
              </div>
              {trackData.status === "dispatched" && (
                <div className="px-4 py-2 bg-orange-50 border-b border-orange-200 text-xs font-mono text-orange-800 flex flex-wrap gap-4">
                  <div><span className="text-[10px] uppercase tracking-wider text-orange-700 mr-1">Sold to:</span><span className="font-semibold">{trackData.client_name || "—"}</span></div>
                  <div><span className="text-[10px] uppercase tracking-wider text-orange-700 mr-1">On order:</span><span className="font-semibold">{trackData.dispatched_order_no || "—"}</span></div>
                  {trackData.weight_category && (
                    <div><span className="text-[10px] uppercase tracking-wider text-orange-700 mr-1">Weight Basis:</span><span className="font-semibold">{trackData.weight_category === "gross" ? "Gross Weight" : trackData.weight_category === "net" ? "Net Weight" : "100% Net Weight"}</span></div>
                  )}
                </div>
              )}
              <div className="p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Activity Timeline</div>
                <ol className="relative border-l-2 border-orange-200 ml-2">
                  {(trackData.events || []).map((ev, i) => (
                    <li key={i} className="pl-4 pb-4 relative" data-testid={`event-${i}`}>
                      <span className="absolute -left-[7px] top-1 h-3 w-3 bg-orange-600 border-2 border-white" />
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{(ev.at || "").slice(0, 19).replace("T", " ")} · {ev.kind}</div>
                      <div className="text-sm font-semibold text-zinc-950">{ev.title}</div>
                      {ev.detail && <div className="text-xs text-zinc-700">{ev.detail}</div>}
                      {ev.by && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">by {ev.by}</div>}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </div>
      )}
      {tab === "customers" && (
        <div className="space-y-3" data-testid="customer-dispatches">
          <div className="bg-white border border-zinc-300 px-4 py-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Per-customer Dispatch History</div>
            <div className="text-xs text-zinc-600 mt-1">Every carton that has left the plant, grouped by the customer who received it. Click a row to expand the full carton list.</div>
          </div>
          {dispatchGroups.length === 0 ? (
            <div className="bg-white border border-zinc-300 text-center text-zinc-500 py-12 text-sm font-sans">No dispatched cartons yet.</div>
          ) : dispatchGroups.map((g, idx) => {
            const key = g.customer_id || `unk-${idx}`;
            const open = !!expandedCust[key];
            return (
              <div key={key} className="bg-white border border-zinc-300" data-testid={`cust-group-${key}`}>
                <button
                  onClick={() => setExpandedCust((s) => ({ ...s, [key]: !s[key] }))}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-50 text-left"
                  data-testid={`cust-toggle-${key}`}
                >
                  <div className="flex items-center gap-3">
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <div>
                      <div className="font-semibold text-zinc-950">{g.company || g.name || "—"}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{g.name}</div>
                    </div>
                  </div>
                  <div className="text-right text-xs font-mono">
                    <div className="text-orange-700 font-semibold text-base">{g.total_cartons} cartons</div>
                    <div className="text-zinc-600">Net {g.total_net_kg.toFixed(2)} · Gross {g.total_gross_kg.toFixed(2)} kg</div>
                    <div className="text-[10px] text-zinc-500">Last dispatch · {(g.last_dispatch_at || "").slice(0, 10)}</div>
                  </div>
                </button>
                {open && (
                  <div className="border-t border-zinc-200" data-testid={`cust-body-${key}`}>
                    {g.challans.length === 0 ? (
                      <div className="px-4 py-6 text-center text-zinc-500 text-sm font-sans">No challans yet.</div>
                    ) : (
                      <>
                        {/* Challan picker list */}
                        <div className="bg-zinc-50 border-b border-zinc-200 px-4 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                          Select a Delivery Challan to view its cartons
                        </div>
                        <div className="divide-y divide-zinc-100">
                          {g.challans.map((ch, ci) => {
                            const chKey = `${ch.order_no}@${ch.dispatched_date}`;
                            const isSel = selectedChallan[key] === chKey;
                            return (
                              <button
                                key={ci}
                                onClick={() => setSelectedChallan((s) => ({ ...s, [key]: isSel ? null : chKey }))}
                                data-testid={`challan-row-${ch.order_no}-${ch.dispatched_date}`}
                                className={`w-full text-left px-4 py-2 flex items-center justify-between transition-colors ${isSel ? "bg-orange-50 hover:bg-orange-100" : "hover:bg-zinc-50"}`}
                              >
                                <div className="flex items-center gap-3">
                                  <FileText size={12} className={isSel ? "text-orange-700" : "text-zinc-500"} />
                                  <div>
                                    <div className={`text-sm font-mono font-semibold ${isSel ? "text-orange-800" : "text-zinc-900"}`}>{ch.order_no}</div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Delivery Challan · {ch.dispatched_date}</div>
                                  </div>
                                </div>
                                <div className="text-xs font-mono text-right">
                                  <div className={`font-semibold ${isSel ? "text-orange-700" : "text-zinc-900"}`}>{ch.cartons_count} cartons</div>
                                  <div className="text-zinc-600">Net {ch.net_kg.toFixed(2)} · Gross {ch.gross_kg.toFixed(2)} kg</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>

                        {/* Selected challan detail */}
                        {(() => {
                          const sel = g.challans.find((c) => `${c.order_no}@${c.dispatched_date}` === selectedChallan[key]);
                          if (!sel) return null;
                          return (
                            <div className="border-t border-zinc-200" data-testid={`challan-detail-${sel.order_no}-${sel.dispatched_date}`}>
                              <div className="px-4 py-2 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
                                <div className="text-[10px] font-mono uppercase tracking-widest text-orange-800">Cartons in Challan {sel.order_no} · {sel.dispatched_date}</div>
                                <div className="text-[11px] font-mono text-orange-800">{sel.cartons_count} cartons · Net {sel.net_kg.toFixed(2)} · Gross {sel.gross_kg.toFixed(2)} kg</div>
                              </div>
                              <table className="w-full text-sm border-collapse">
                                <thead className="bg-zinc-50 text-zinc-500 uppercase text-[9px] font-mono tracking-wider">
                                  <tr>
                                    <th className="text-left px-3 py-1.5 border-b border-zinc-200">Box No</th>
                                    <th className="text-left px-3 py-1.5 border-b border-zinc-200">Category</th>
                                    <th className="text-left px-3 py-1.5 border-b border-zinc-200">Weight Basis</th>
                                    <th className="text-left px-3 py-1.5 border-b border-zinc-200">Produced</th>
                                    <th className="text-left px-3 py-1.5 border-b border-zinc-200">From</th>
                                    <th className="text-right px-3 py-1.5 border-b border-zinc-200">Net</th>
                                    <th className="text-right px-3 py-1.5 border-b border-zinc-200">Gross</th>
                                  </tr>
                                </thead>
                                <tbody className="font-mono">
                                  {sel.cartons.map((c) => {
                                    const wcatLabel = c.weight_category === "gross" ? "Gross Weight" : c.weight_category === "net" ? "Net Weight" : "100% Net Weight";
                                    return (
                                      <tr
                                        key={c.box_no}
                                        onClick={() => { setTab("track"); setTrackQuery(c.box_no); lookupCarton(c.box_no); }}
                                        className="border-b border-zinc-200 hover:bg-orange-50 cursor-pointer"
                                        title="Open in Carton Tracker"
                                      >
                                        <td className="px-3 py-2 font-semibold text-orange-700">{c.box_no}</td>
                                        <td className="px-3 py-2 text-xs">{c.sku}</td>
                                        <td className="px-3 py-2 text-xs"><span className="inline-block border border-zinc-300 bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-wider">{wcatLabel}</span></td>
                                        <td className="px-3 py-2 text-xs">{c.production_date}</td>
                                        <td className="px-3 py-2 text-xs"><MapPin size={9} className="inline" /> {c.from_location}</td>
                                        <td className="px-3 py-2 text-right">{c.net_weight_kg.toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-orange-700">{c.gross_weight_kg.toFixed(2)}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
